import type { ResolvedLocation } from './types/HelperTypes';

export type { ResolvedLocation };

function isValidCoordinate(lat: number, lng: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

async function fetchJsonWithTimeout(url: string, timeoutMs: number): Promise<any> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) {
      throw new Error(`Location provider request failed (${response.status})`);
    }
    return await response.json();
  } finally {
    window.clearTimeout(timer);
  }
}

async function requestIpApiLocation(): Promise<ResolvedLocation> {
  const data = await fetchJsonWithTimeout('https://ipapi.co/json/', 7000);
  const lat = Number(data?.latitude);
  const lng = Number(data?.longitude);
  if (!isValidCoordinate(lat, lng)) {
    throw new Error('ipapi returned invalid coordinates');
  }
  return { lat, lng, source: 'ipapi' };
}

async function requestIpWhoIsLocation(): Promise<ResolvedLocation> {
  const data = await fetchJsonWithTimeout('https://ipwho.is/', 7000);
  if (data?.success === false) {
    throw new Error(data?.message || 'ipwho.is lookup failed');
  }
  const lat = Number(data?.latitude);
  const lng = Number(data?.longitude);
  if (!isValidCoordinate(lat, lng)) {
    throw new Error('ipwho.is returned invalid coordinates');
  }
  return { lat, lng, source: 'ipwhois' };
}

/**
 * Coarse location from IP-based providers only (no GPS / no browser permission dialog).
 */
export async function resolveApproximateNetworkLocation(): Promise<ResolvedLocation> {
  try {
    return await requestIpApiLocation();
  } catch {
    return await requestIpWhoIsLocation();
  }
}

/** @deprecated Use resolveApproximateNetworkLocation — browser geolocation is not used. */
export async function resolveCurrentLocation(): Promise<ResolvedLocation> {
  return resolveApproximateNetworkLocation();
}
